import { useTranslation } from 'react-i18next';
import { useMatchRoute } from '@tanstack/react-router';
import { css } from '@emotion/react';
import { Button, Icon } from '@anytag/shared-atoms';
import { AddTagModal, DeleteTagModal } from '@anytag/shared-molecules';
import { cn, isJapanCountry } from '@anytag/shared-utils';
import { TagType } from '../schemaTypes';
import { useManageTags, ManageTagsProps } from './hooks';

export interface TagsListProps extends ManageTagsProps {
  tagType?: TagType;
  countryId: string;
  className?: string;
  creating?: boolean;
  deleting?: boolean;
}

export const TagsList = ({
  tags,
  onSubmit,
  creating,
  deleting,
  className,
  countryId,
  onCreateTag,
  onDeleteTag,
  tagType = TagType.HASHTAG
}: TagsListProps) => {
  const { t } = useTranslation();
  const matchPath = useMatchRoute();
  const {
    addModalState,
    deletingTag,
    setDeletingTag,
    handleSelectTag,
    handleDeleteTag,
    handleCreateTag,
    resetDeletingTag,
    checkActivatingTag
  } = useManageTags({ tags, onCreateTag, onDeleteTag, onSubmit });

  const isInstagramHashtagsAnalyticsForJP = matchPath({ to: '/hashtag-analytics/instagram' }); // The default route is for JP
  // Limitation: https://anymindgroup.atlassian.net/wiki/spaces/NC/pages/3520823305/Hashtag+tag+analysis+extension+for+new+dashboard#Upper-limit-number-of-hashtags-to-be-registered
  const limit = isInstagramHashtagsAnalyticsForJP && isJapanCountry(countryId) ? 300 : 50;
  const disabledAddButton = tags.length >= limit;
  const isKeywordTag = tagType === TagType.KEYWORD;

  return (
    <>
      <div css={styles.wrapper} className={className}>
        <div css={styles.itemWrapper}>
          <Button
            title="+"
            variant="outline"
            onClick={addModalState.open}
            disabled={disabledAddButton}
            className={cn(styles.item(false), 'p-0')}
            tooltip={{
              disabled: false,
              help: disabledAddButton ? t('Can’t add tags more than', { count: limit }) : t('Tooltip.Add Hashtag')
            }}
          />
        </div>

        {tags.map((tag) => {
          const active = checkActivatingTag(tag);

          return (
            <div key={tag.id} css={styles.itemWrapper}>
              <Button
                className={styles.item(active)}
                variant={active ? 'solid' : 'outline'}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectTag(tag);
                }}
              >
                <p css={styles.itemName(active)}>{tag.label}</p>

                <div
                  css={styles.icon}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeletingTag(tag);
                  }}
                >
                  <Icon size={8} icon="close" variant="inherit" />
                </div>
              </Button>
            </div>
          );
        })}
      </div>

      <AddTagModal
        loading={creating}
        onAdd={handleCreateTag}
        open={addModalState.status}
        onClose={addModalState.close}
        title={isKeywordTag ? t('Dialog.Add Keyword') : t('Dialog.Add Hashtag')}
        description={
          isKeywordTag
            ? t('Dialog.Enter the name of the keyword you wish to add')
            : t('Dialog.Enter the name of the hashtag you wish to add')
        }
      />

      <DeleteTagModal
        loading={deleting}
        open={!!deletingTag?.id}
        tag={deletingTag?.label}
        onClose={resetDeletingTag}
        onDelete={handleDeleteTag}
        title={isKeywordTag ? t('Dialog.Delete Keyword') : t('Dialog.Delete Hashtag')}
        description={isKeywordTag ? t('Dialog.DeleteKeywordWarning') : t('Dialog.DeleteComparableHashtagWarning')}
      />
    </>
  );
};
const styles = {
  wrapper: css({
    margin: '-4px',
    display: 'flex',
    flexWrap: 'wrap',
    boxSizing: 'border-box'
  }),
  itemWrapper: css({
    padding: '4px',
    boxSizing: 'border-box'
  }),
  item: (active?: boolean) =>
    cn('min-w-30 pr-3 pl-4 text-primary', {
      'text-white': active
    }),
  itemName: (active?: boolean) =>
    css({
      flex: 1,
      fontSize: '12px',
      lineHeight: '100%',
      letterSpacing: '0.5px',
      fontWeight: active ? 600 : 400
    }),
  icon: css({
    flex: 'none',
    width: '12px',
    height: '12px',
    display: 'flex',
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'center'
  })
};
